import { collection, doc, setDoc, writeBatch } from 'firebase/firestore';
import { IComparisonPopulationSetting } from '../../../providers/settings/comparisonPopulationSettingsProvider';
import { IDateRange } from '../../../providers/settings/dates/dateRangeProvider';
import { IComparisonReportSettings } from '../../../providers/settings/reports/comparisonReportSettingsProvider';
import { ISettingsTemplate } from '../../../providers/settings/reports/types';
import { userAsPromise } from '../../../providers/userProvider';

/**
 * @description saves the template as the selected template and additional settings
 * @param template
 */
export const saveComparisonSettings = async (
  template: ISettingsTemplate<IComparisonReportSettings>,
  targetTimeframe: IDateRange,
  comparison: IComparisonPopulationSetting
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/comparisonReport`
  );

  await setDoc(docRef, {
    selected: template ?? null,
    targetTimeframe: targetTimeframe ?? null,
    comparison,
  });
};

/**
 * @description updates the comparison template
 * @param template
 * @param isSelected
 */
export const updateComparisonTemplate = async (
  template: ISettingsTemplate<IComparisonReportSettings>,
  isSelected: boolean
): Promise<void> => {
  const user = await userAsPromise();

  if (isSelected) {
    const batch = writeBatch(user.services.firestore);
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/comparisonReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/comparisonReport`
      ),
      { selected: template },
      { merge: true }
    );
    await batch.commit();
  } else {
    await setDoc(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/comparisonReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
  }
};

/**
 * @description delete the template and change the current template to another one
 * @param template
 * @param replacement
 * @returns
 */
export const deleteSelectedTemplate = async (
  template: ISettingsTemplate<IComparisonReportSettings>,
  replacement: ISettingsTemplate<IComparisonReportSettings>
): Promise<void> => {
  const user = await userAsPromise();

  const batch = writeBatch(user.services.firestore);
  const updateRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/comparisonReport/templates/${template.id}`
  );
  const update: ISettingsTemplate<IComparisonReportSettings> = {
    ...template,
    active: false,
  };
  batch.update(updateRef, update as unknown as Record<string, unknown>);
  const selectedRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/comparisonReport`
  );
  batch.set(selectedRef, { selected: replacement ?? null }, { merge: true });
  return batch.commit();
};

/**
 * @description creates a new comparison report settings template
 * @param template
 */
export const createComparisonTemplate = async (
  template: Partial<ISettingsTemplate<IComparisonReportSettings>>
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    collection(
      user.services.firestore,
      `userProfiles/${user.id}/settings/comparisonReport/templates`
    )
  );
  const newDocument = {
    ...template,
    id: docRef.id,
    active: true,
  };
  await setDoc(docRef, newDocument, { merge: true });
};
